// Standard Algebraic Notation. Encoding needs the position (for
// disambiguation and the check suffix); decoding matches the input against
// the SAN of every legal move, so the two directions can never disagree.
import { applyMove } from './applyMove.js';
import { isInCheck } from './attacks.js';
import { generateLegalMoves } from './legalMoves.js';
import { EMPTY, FLAG_CAPTURE, FLAG_CASTLE_KING, FLAG_CASTLE_QUEEN, PAWN, typeOf, type Move, type Position } from './types.js';
import { fileOf, parseSquare, rowOf, squareName } from './squares.js';

const PIECE_LETTERS = ['', '', 'N', 'B', 'R', 'Q', 'K'];

function disambiguation(move: Move, legal: readonly Move[]): string {
  const rivals = legal.filter((m) => m.piece === move.piece && m.to === move.to && m.from !== move.from);
  if (rivals.length === 0) return '';
  const from = squareName(move.from);
  if (rivals.every((m) => fileOf(m.from) !== fileOf(move.from))) return from.charAt(0);
  if (rivals.every((m) => rowOf(m.from) !== rowOf(move.from))) return from.charAt(1);
  return from;
}

/** SAN without the check suffix. */
function sanBody(move: Move, legal: readonly Move[]): string {
  if ((move.flags & FLAG_CASTLE_KING) !== 0) return 'O-O';
  if ((move.flags & FLAG_CASTLE_QUEEN) !== 0) return 'O-O-O';
  const capture = (move.flags & FLAG_CAPTURE) !== 0 ? 'x' : '';
  const to = squareName(move.to);
  if (typeOf(move.piece) === PAWN) {
    const fromFile = capture === '' ? '' : squareName(move.from).charAt(0);
    const promo = move.promotion === EMPTY ? '' : `=${PIECE_LETTERS[move.promotion] ?? ''}`;
    return `${fromFile}${capture}${to}${promo}`;
  }
  return `${PIECE_LETTERS[typeOf(move.piece)] ?? ''}${disambiguation(move, legal)}${capture}${to}`;
}

function checkSuffix(pos: Position, move: Move): string {
  const after = applyMove(pos, move);
  if (!isInCheck(after.board, after.turn)) return '';
  return generateLegalMoves(after).length === 0 ? '#' : '+';
}

/** SAN for a legal move, e.g. "Nbd7", "exd6", "e8=Q+", "O-O#". */
export function moveToSan(pos: Position, move: Move, legal: readonly Move[] = generateLegalMoves(pos)): string {
  return sanBody(move, legal) + checkSuffix(pos, move);
}

/** Strip annotations and normalise spellings: 0-0 → O-O, e8Q → e8=Q. */
function normalise(san: string): string {
  return san
    .trim()
    .replace(/[+#!?]+$/, '')
    .replace(/^0-0-0/, 'O-O-O')
    .replace(/^0-0/, 'O-O')
    .replace(/([a-h][18])=?([NBRQnbrq])$/, (_m, sq: string, p: string) => `${sq}=${p.toUpperCase()}`);
}

const LOOSE_SAN = /^([NBRQK])?([a-h])?([1-8])?x?([a-h][1-8])(?:=([NBRQ]))?$/;

/** Fallback for valid-but-unusual input: over-disambiguation like "Ngf3", "Qd1d2". */
function looseMatch(text: string, legal: readonly Move[]): Move | undefined {
  const m = LOOSE_SAN.exec(text);
  if (m === null) return undefined;
  const [, pieceLetter, fileHint, rankHint, toName = '', promoLetter] = m;
  const pieceType = pieceLetter === undefined ? PAWN : PIECE_LETTERS.indexOf(pieceLetter);
  const to = parseSquare(toName);
  const promotion = promoLetter === undefined ? EMPTY : PIECE_LETTERS.indexOf(promoLetter);
  const candidates = legal.filter((mv) => {
    const from = squareName(mv.from);
    return typeOf(mv.piece) === pieceType && mv.to === to && mv.promotion === promotion &&
      (fileHint === undefined || from.charAt(0) === fileHint) &&
      (rankHint === undefined || from.charAt(1) === rankHint);
  });
  return candidates.length === 1 ? candidates[0] : undefined;
}

/** The legal move a SAN string names, or undefined if none (or it is ambiguous). */
export function sanToMove(pos: Position, san: string, legal: readonly Move[] = generateLegalMoves(pos)): Move | undefined {
  const text = normalise(san);
  const exact = legal.find((m) => sanBody(m, legal) === text);
  return exact ?? looseMatch(text, legal);
}
